import { AVATARS } from "@shared/palette";
import { MAX_NAME } from "@shared/room";
import { useState } from "react";
import { useI18n } from "../i18n";
import { avatarVoice, haptic } from "../lib/sfx";
import { Avatar } from "./Avatar";

export interface Identity {
  name: string;
  avatar: number;
}

/** Name + avatar picker. Every avatar answers with its own voice when picked. */
export function IdentityForm({ value, onChange, autoFocus, taken = [] }: { value: Identity; onChange(v: Identity): void; autoFocus?: boolean; taken?: number[] }) {
  const { d } = useI18n();
  const [bump, setBump] = useState<number | null>(null);
  return (
    <div className="space-y-5">
      <div>
        <label className="label" htmlFor="id-name">{d.create.yourName}</label>
        <input
          id="id-name"
          className="field"
          value={value.name}
          maxLength={MAX_NAME}
          placeholder={d.create.namePh}
          autoFocus={autoFocus}
          autoComplete="nickname"
          enterKeyHint="done"
          onChange={(e) => onChange({ ...value, name: e.target.value })}
        />
      </div>
      <div>
        <span className="label">{d.create.avatar}</span>
        <AvatarGrid
          value={value.avatar}
          taken={taken}
          bump={bump}
          onPick={(i) => {
            onChange({ ...value, avatar: i });
            setBump(i);
            avatarVoice(i);
            haptic(10);
            setTimeout(() => setBump((b) => (b === i ? null : b)), 650);
          }}
        />
      </div>
    </div>
  );
}

export function AvatarGrid({ value, onPick, taken = [], bump }: { value: number; onPick(i: number): void; taken?: number[]; bump?: number | null }) {
  const { d } = useI18n();
  return (
    <div className="grid grid-cols-5 gap-2 sm:grid-cols-7" role="radiogroup" aria-label={d.create.avatar}>
      {AVATARS.map((a, i) => {
        const on = value === i;
        const busy = taken.includes(i) && !on;
        return (
          <button
            key={a.id}
            type="button"
            role="radio"
            aria-checked={on}
            aria-disabled={busy || undefined}
            disabled={busy}
            aria-label={(d.avatars as Record<string, string>)[a.id] ?? a.id}
            title={(d.avatars as Record<string, string>)[a.id] ?? a.id}
            className={`grid place-items-center rounded-2xl p-1.5 transition-colors ${busy ? "cursor-not-allowed" : ""}`}
            style={{
              background: on ? "var(--color-ground-5)" : "var(--color-ground-3)",
              opacity: busy ? 0.3 : 1,
              filter: busy ? "grayscale(1)" : undefined,
              boxShadow: on ? "inset 0 0 0 3px var(--color-flame)" : undefined,
            }}
            onClick={() => !busy && onPick(i)}
          >
            <Avatar index={i} size={50} disc={false} mood={bump === i ? "pick" : "still"} />
          </button>
        );
      })}
    </div>
  );
}
